import { X, Loader2, Check } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { paypal as paypalApi } from "@/lib/api";

const PaymentMethodModal = ({ open, course, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  if (!open || !course) return null;

  const payWithPayPal = async () => {
    setLoading(true);
    try {
      const res = await paypalApi.createOrder(course.id, window.location.origin);
      if (res.approve_url) window.location.href = res.approve_url;
    } catch {
      alert(t("payment.errorPaypal"));
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="payment-method-modal"
      className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md p-8 md:p-10 relative"
      >
        <button
          onClick={onClose}
          data-testid="pay-modal-close-btn"
          className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-900"
        >
          <X className="w-5 h-5" />
        </button>

        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#4F46E5] mb-3">
          {t("payment.title")}
        </p>
        <h2 className="font-display font-black text-2xl tracking-tighter mb-1">
          {course.title}
        </h2>
        <p className="text-zinc-500 text-sm mb-8">
          {t("payment.single")} · <span className="font-bold text-zinc-900">${course.price} USD</span>
        </p>

        {/* PayPal Checkout: includes PayPal, Apple Pay, Cards */}
        <button
          onClick={payWithPayPal}
          disabled={loading}
          data-testid="pay-paypal-btn"
          className="w-full flex items-center justify-center gap-3 bg-[#FFC439] hover:bg-[#F0B72F] text-[#003087] transition-all p-4 disabled:opacity-50 mb-5"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <span className="font-bold text-xl" style={{ fontFamily: "Arial, sans-serif" }}>
              <span style={{ color: "#003087" }}>Pay</span>
              <span style={{ color: "#009cde" }}>Pal</span>
            </span>
          )}
        </button>

        {/* What's included via PayPal Checkout */}
        <div className="bg-zinc-50 border border-zinc-200 p-4 mb-6 space-y-2">
          <p className="text-xs uppercase tracking-wider font-bold text-zinc-700 mb-2">
            {t("payment.includes")}
          </p>
          <div className="flex items-center gap-2 text-sm text-zinc-700">
            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span>{t("payment.viaPaypalAccount")}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-700">
            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span className="inline-flex items-center gap-1.5">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
              {t("payment.viaApplePay")}
            </span>
          </div>
          <div className="flex items-center gap-2 text-sm text-zinc-700">
            <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <span>{t("payment.viaCard")}</span>
          </div>
        </div>

        <div className="pt-4 border-t border-zinc-200 flex items-start gap-2 text-xs text-zinc-500">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0 mt-0.5">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0110 0v4" />
          </svg>
          <span>{t("payment.secure")}</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodModal;
